// Settings -> People: the unified identity platform's directory (documentation/identity-
// architecture.md). One person = one RadioOS user with assignments in scopes; people are invited,
// never created with a password here. What a caller may assign comes from /api/identity/options
// (the delegation policy); the server decides every change again.
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  addAssignment,
  cancelInvitation,
  getIdentityOptions,
  invitePerson,
  listPeople,
  deletePerson,
  reissueInvitation,
  removeAssignment,
  setPersonDisabled,
  type AssignmentView,
  type IdentityOptions,
  type IssuedInvitation,
  type Person,
} from '../../services/identityPlatform';
import { ApiError } from '../../services/tenancy';
import { Dialog } from '../../components/Dialog';
import {
  INVITE_EXPLANATION,
  METHOD_LABELS,
  ROLE_HINTS,
  capabilityLabel,
  deleteAction,
  deleteErrorText,
  deliveryLabel,
  describeAssignment,
  describePersonDelete,
  personDeleteConfirmed,
  draftToSpec,
  emptyDraft,
  personActions,
  scopeOfRole,
  statusPresentation,
  type AssignmentDraft,
} from './peopleModel';

const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';
const input = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm w-full';
const toneClass = { green: 'text-green-300', yellow: 'text-yellow-200', red: 'text-red-300', gray: 'text-gray-400' } as const;

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'The request could not be completed. Please try again.';
}


function when(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export type PersonAction = 'reissue' | 'cancel' | 'disable' | 'enable' | 'assign' | 'delete';

export function PeopleTable({ people, busy, onAction, onRemoveAssignment }: {
  people: Person[]; busy?: boolean;
  onAction: (p: Person, a: PersonAction) => void; onRemoveAssignment: (p: Person, a: AssignmentView) => void;
}) {
  if (people.length === 0) return <p className="text-sm text-gray-400">Nobody here yet.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-gray-400">
        <tr>
          <th scope="col" className="py-1">Person</th><th scope="col" className="py-1">Status</th><th scope="col" className="py-1">Access</th>
          <th scope="col" className="py-1">Sign-in</th><th scope="col" className="py-1">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700 align-top">
        {people.map((p) => {
          const st = statusPresentation(p);
          const act = personActions(p);
          const del = deleteAction(p);
          return (
            <tr key={p.id}>
              <td className="py-1.5">
                <div>{p.display_name || p.email}{p.self && <span className="ml-2 text-xs text-blue-300">(you)</span>}</div>
                {p.display_name && <div className="text-xs text-gray-400 break-all">{p.email}</div>}
                {p.legacy && <div className="text-xs text-gray-400">Legacy network account - managed on the network Users page</div>}
              </td>
              <td className={`py-1.5 ${toneClass[st.tone]}`}>
                {st.label}
                {p.status === 'invited' && p.invitation && (
                  <div className="text-xs text-gray-400">{deliveryLabel(p.invitation.delivery_state)}{p.invitation.expired ? '' : ` · until ${when(p.invitation.expires_at)}`}</div>
                )}
              </td>
              <td className="py-1.5">
                {p.assignments.length === 0 ? <span className="text-gray-400">No assignment</span> : (
                  <ul className="space-y-1">
                    {p.assignments.map((a) => (
                      <li key={a.id} className="flex items-start gap-2">
                        <span>{describeAssignment(a)}</span>
                        {a.manageable && act.assign && (
                          <button className="text-xs text-red-300 hover:underline" disabled={busy} onClick={() => onRemoveAssignment(p, a)} aria-label={`Remove ${describeAssignment(a)}`}>remove</button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td className="py-1.5 text-gray-300">
                {p.methods.length === 0 ? '—' : p.methods.map((m) => METHOD_LABELS[m] || m).join(', ')}
                {p.methods.includes('radioos') && <div className="text-xs text-gray-400">{p.mfa_enrolled ? 'Authenticator app set up' : 'No authenticator app yet'}</div>}
                <div className="text-xs text-gray-400">Last sign-in: {when(p.last_login_at)}</div>
              </td>
              <td className="py-1.5">
                <div className="flex flex-wrap gap-2">
                  {act.assign && <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onAction(p, 'assign')}>Add access…</button>}
                  {act.reissue && <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onAction(p, 'reissue')} title="Sends a new one-time link; the previous link stops working">New invitation link</button>}
                  {act.cancel && <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onAction(p, 'cancel')}>Cancel invitation</button>}
                  {act.disable && <button className={`${btn} bg-red-900 hover:bg-red-800`} disabled={busy} onClick={() => onAction(p, 'disable')}>Disable</button>}
                  {act.enable && <button className={`${btn} bg-green-700 hover:bg-green-600`} disabled={busy} onClick={() => onAction(p, 'enable')}>Enable</button>}
                </div>
                {del.show && (
                  // Permanent deletion: separated from the other actions and from "remove" (one access).
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <button className={`${btn} border border-red-600 text-red-300 hover:bg-red-950`} disabled={busy || !del.enabled}
                      title={del.reason || 'Permanently deletes this person: all sign-in methods and all access'} onClick={() => onAction(p, 'delete')}>
                      Delete person…
                    </button>
                    {del.reason && <p className="text-xs text-gray-400 mt-1">{del.reason}</p>}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Role, scope, capabilities and departments of one assignment (only what options allow). */
export function AssignmentEditor({ options, draft, onChange }: { options: IdentityOptions; draft: AssignmentDraft; onChange: (d: AssignmentDraft) => void }) {
  const scope = scopeOfRole(options, draft.role);
  const oems = options.oem_partners.filter((o) => o.roles.includes(draft.role));
  const customers = options.customers.filter((c) => c.roles.includes(draft.role));
  const customer = options.customers.find((c) => c.id === draft.scopeId);
  const withCapabilities = draft.role === 'customer_user' || draft.role === 'oem_user';
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="assignment-role" className="block text-xs text-gray-300 mb-1">Role</label>
        <select id="assignment-role" className={input} value={draft.role} onChange={(e) => onChange({ ...emptyDraft(), role: e.target.value })}>
          <option value="">Choose a role…</option>
          {options.roles.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
        </select>
        {draft.role && <p className="text-xs text-gray-400 mt-1">{ROLE_HINTS[draft.role]}</p>}
      </div>
      {scope === 'oem' && (
        <div>
          <label htmlFor="assignment-oem" className="block text-xs text-gray-300 mb-1">OEM Partner</label>
          <select id="assignment-oem" className={input} value={draft.scopeId} onChange={(e) => onChange({ ...draft, scopeId: e.target.value })}>
            <option value="">Choose…</option>
            {oems.map((o) => <option key={o.id} value={o.id}>{o.name}{o.status !== 'active' ? ' (disabled)' : ''}</option>)}
          </select>
        </div>
      )}
      {scope === 'customer' && (
        <div>
          <label htmlFor="assignment-customer" className="block text-xs text-gray-300 mb-1">Customer</label>
          <select id="assignment-customer" className={input} value={draft.scopeId} onChange={(e) => onChange({ ...draft, scopeId: e.target.value, departments: [] })}>
            <option value="">Choose…</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
      {withCapabilities && (
        <fieldset>
          <legend className="text-xs text-gray-300 mb-1">Capabilities</legend>
          <div className="grid grid-cols-2 gap-1">
            {options.capabilities.filter((c) => c !== 'tenant.view').map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.capabilities.includes(c)} onChange={() => onChange({ ...draft, capabilities: toggle(draft.capabilities, c) })} />
                {capabilityLabel(c)}
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">Dispatch is a capability. You can only give capabilities you hold yourself in this scope.</p>
        </fieldset>
      )}
      {draft.role === 'customer_user' && customer && (
        <fieldset>
          <legend className="text-xs text-gray-300 mb-1">Where</legend>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={!draft.restricted} onChange={() => onChange({ ...draft, restricted: false, departments: [] })} /> The whole customer
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={draft.restricted} onChange={() => onChange({ ...draft, restricted: true })} /> Only these departments
          </label>
          {draft.restricted && (
            <div className="ml-6 mt-1 space-y-1">
              {customer.departments.length === 0 && <p className="text-xs text-yellow-200">This customer has no departments.</p>}
              {customer.departments.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={draft.departments.includes(d.id)} onChange={() => onChange({ ...draft, departments: toggle(draft.departments, d.id) })} />
                  {d.name}
                </label>
              ))}
              <p className="text-xs text-gray-400">If all of these departments are deleted later, the access ends (it never widens to the whole customer).</p>
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}

export function InviteDialog({ options, error, busy, onInvite, onCancel }: {
  options: IdentityOptions; error?: string | null; busy?: boolean;
  onInvite: (email: string, name: string, draft: AssignmentDraft) => void; onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<AssignmentDraft>(emptyDraft());
  const check = draftToSpec(options, draft);
  return (
    <Dialog title="Invite a person" onCancel={onCancel}>
      <ul className="list-disc ml-5 text-sm text-gray-300 space-y-1">{INVITE_EXPLANATION.map((l) => <li key={l}>{l}</li>)}</ul>
      <label htmlFor="invite-email" className="block text-xs text-gray-300">E-mail address</label>
      <input id="invite-email" type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
      <label htmlFor="invite-name" className="block text-xs text-gray-300">Name</label>
      <input id="invite-name" className={input} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      <AssignmentEditor options={options} draft={draft} onChange={setDraft} />
      {check.problem && draft.role && <p className="text-xs text-yellow-200">{check.problem}</p>}
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      <button type="submit" className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={busy || !email.includes('@') || !check.spec} onClick={() => onInvite(email.trim(), name.trim(), draft)}>
        Send invitation
      </button>
    </Dialog>
  );
}

export function AssignDialog({ person, options, error, busy, onAssign, onCancel }: {
  person: Person; options: IdentityOptions; error?: string | null; busy?: boolean;
  onAssign: (draft: AssignmentDraft) => void; onCancel: () => void;
}) {
  const [draft, setDraft] = useState<AssignmentDraft>(emptyDraft());
  const check = draftToSpec(options, draft);
  return (
    <Dialog title={`Add access for ${person.display_name || person.email}`} onCancel={onCancel}>
      <AssignmentEditor options={options} draft={draft} onChange={setDraft} />
      {check.problem && draft.role && <p className="text-xs text-yellow-200">{check.problem}</p>}
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      <button type="submit" className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={busy || !check.spec} onClick={() => onAssign(draft)}>Add access</button>
    </Dialog>
  );
}

/** Delete person: who, how they sign in, what they can access, what happens; the e-mail address
 * typed to confirm (the server checks it again and decides the authority). */
export function DeletePersonDialog({ person, typed, error, busy, onTyped, onCancel, onConfirm }: {
  person: Person; typed: string; error?: string | null; busy?: boolean;
  onTyped: (v: string) => void; onCancel: () => void; onConfirm: () => void;
}) {
  const d = describePersonDelete(person);
  const ready = personDeleteConfirmed(typed, person);
  return (
    <div data-management-modal role="dialog" aria-modal="true" aria-labelledby="person-delete-title" className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <form className="bg-gray-800 border border-red-700 rounded-lg w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onSubmit={(e) => { e.preventDefault(); if (ready && !busy) onConfirm(); }}>
        <h3 id="person-delete-title" className="text-lg font-semibold text-red-200">{d.title}</h3>
        <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-sm">
          {d.facts.map(([k, v]) => (<Fragment key={k}><dt className="text-gray-400">{k}</dt><dd className="col-span-2 break-all">{v}</dd></Fragment>))}
          <dt className="text-gray-400">Access</dt>
          <dd className="col-span-2"><ul className="space-y-0.5">{d.access.map((a) => <li key={a}>{a}</li>)}</ul></dd>
        </dl>
        <ul className="list-disc pl-5 space-y-1 text-sm">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>
        <div>
          <label htmlFor="person-delete-confirm" className="block text-xs text-gray-300 mb-1">
            Type the e-mail address <span className="font-mono text-gray-100">{person.email}</span> to confirm
          </label>
          <input id="person-delete-confirm" className={input} value={typed} autoComplete="off" autoFocus onChange={(e) => onTyped(e.target.value)} />
        </div>
        {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600`} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className={`${btn} bg-red-700 hover:bg-red-600`} disabled={!ready || busy}>{busy ? 'Deleting…' : d.confirm}</button>
        </div>
      </form>
    </div>
  );
}

/** Shows the result of an invitation; the link only when it was not e-mailed - once. */
export function IssuedInvitationDialog({ issued, onClose }: { issued: IssuedInvitation; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = issued.invitation_url;
  return (
    <Dialog title={`Invitation for ${issued.invitation.email}`} onCancel={onClose}>
      <p className="text-gray-300">{issued.notice}</p>
      {url && (
        <>
          <p className="font-semibold text-yellow-200">This invitation link is shown once. It cannot be retrieved again.</p>
          <label htmlFor="invitation-link" className="block text-xs text-gray-300">Invitation link</label>
          <input id="invitation-link" readOnly value={url} className={`${input} font-mono`} onFocus={(e) => e.currentTarget.select()} />
          <button className={`${btn} bg-blue-600 hover:bg-blue-500`} onClick={() => { navigator.clipboard?.writeText(url).then(() => setCopied(true)).catch(() => undefined); }}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </>
      )}
      <p className="text-xs text-gray-400">Valid until {when(issued.invitation.expires_at)}.</p>
    </Dialog>
  );
}

export default function People() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [options, setOptions] = useState<IdentityOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [assigning, setAssigning] = useState<Person | null>(null);
  const [issued, setIssued] = useState<IssuedInvitation | null>(null);
  const [filter, setFilter] = useState('');
  const [deleting, setDeleting] = useState<Person | null>(null);
  const [typed, setTyped] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([listPeople(), getIdentityOptions()])
      .then(([p, o]) => { setPeople(p); setOptions(o); setError(null); })
      .catch((err) => setError(errorText(err)));
  }, []);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>, after?: (r: unknown) => void) => {
    setBusy(true);
    setDialogError(null);
    try {
      const r = await fn();
      after?.(r);
      load();
    } catch (err) {
      setDialogError(errorText(err));
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const onAction = (p: Person, a: PersonAction) => {
    setError(null);
    switch (a) {
      case 'assign':
        setDialogError(null);
        setAssigning(p);
        break;
      case 'reissue':
        run(() => reissueInvitation(p.id), (r) => setIssued(r as IssuedInvitation));
        break;
      case 'cancel':
        if (window.confirm(`Cancel the invitation of ${p.email}? The link stops working; the person keeps no access.`)) run(() => cancelInvitation(p.id));
        break;
      case 'disable':
        if (window.confirm(`Disable ${p.display_name || p.email}? Their sessions end now and they cannot sign in until enabled again. Nothing is deleted.`)) run(() => setPersonDisabled(p.id, true));
        break;
      case 'enable':
        run(() => setPersonDisabled(p.id, false));
        break;
      case 'delete':
        setDialogError(null);
        setTyped('');
        setDeleting(p);
        break;
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    setDialogError(null);
    try {
      const r = await deletePerson(deleting.id, typed.trim());
      setNotice(`${deleting.display_name || deleting.email} was deleted: ${r.person.sign_in_methods.length} sign-in method(s) and ${r.person.assignments_removed.length} access assignment(s) removed, ${r.person.revoked_sessions} session(s) ended. The history remains.`);
      setDeleting(null);
      load();
    } catch (err) {
      setDialogError(deleteErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const shown = (people || []).filter((p) => !filter || `${p.email} ${p.display_name || ''}`.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">People</h2>
          <p className="text-sm text-gray-400">Everyone who can sign in to RadioOS that you administer: their access (assignments) and how they sign in.</p>
        </div>
        {options?.can_invite && (
          <button className={`${btn} bg-blue-600 hover:bg-blue-500`} onClick={() => { setDialogError(null); setInviting(true); }}>Invite a person…</button>
        )}
      </div>
      <input aria-label="Filter people" placeholder="Filter by name or e-mail" className={input} value={filter} onChange={(e) => setFilter(e.target.value)} />
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      {notice && <p role="status" className="text-sm text-green-300">{notice} <button className="underline ml-2" onClick={() => setNotice(null)}>Dismiss</button></p>}
      {people === null ? <p className="text-sm text-gray-400">Loading…</p> : (
        <PeopleTable people={shown} busy={busy} onAction={onAction}
          onRemoveAssignment={(p, a) => {
            const last = p.assignments.length === 1;
            if (window.confirm(`Remove "${describeAssignment(a)}" from ${p.display_name || p.email}?${last ? ' It is their last access: they will be disabled (not deleted).' : ''}`)) run(() => removeAssignment(a.id));
          }} />
      )}
      {inviting && options && (
        <InviteDialog options={options} error={dialogError} busy={busy} onCancel={() => setInviting(false)}
          onInvite={(email, name, draft) => {
            const spec = draftToSpec(options, draft).spec;
            if (spec) run(() => invitePerson(email, name, [spec]), (r) => { setInviting(false); setIssued(r as IssuedInvitation); });
          }} />
      )}
      {assigning && options && (
        <AssignDialog person={assigning} options={options} error={dialogError} busy={busy} onCancel={() => setAssigning(null)}
          onAssign={(draft) => {
            const spec = draftToSpec(options, draft).spec;
            if (spec) run(() => addAssignment(assigning.id, spec), () => setAssigning(null));
          }} />
      )}
      {issued && <IssuedInvitationDialog issued={issued} onClose={() => setIssued(null)} />}
      {deleting && (
        <DeletePersonDialog person={deleting} typed={typed} error={dialogError} busy={busy} onTyped={setTyped}
          onCancel={() => setDeleting(null)} onConfirm={confirmDelete} />
      )}
    </div>
  );
}
